import { percent, percentage } from "@hlcr/core/numeric";
import { ProcessedFile } from "@hlcr/mui/Upload";
import { useIntl } from "@hlcr/ui/Intl";
import React, { useState } from "react";
import { useDispatch } from "react-redux";

import ModalWindow from "components/ModalWindow/ModalWindow";
import PointSlider from "components/Solution/PointSlider";
import { exportEvent, onParticipantChange, submitModal } from "grading/bulkActionModal.functions";
import { useEventParticipantTeamsToBeGraded, useEventParticipantUsersToBeGraded, useSelectedTeamIds, useSelectedUserIds } from "grading/bulkActionModal.hooks";
import { BulkActionModalButtonBar } from "grading/BulkActionModalButtonBar";
import { BulkActionModalHeader } from "grading/BulkActionModalHeader";
import { EventParticipant } from "grading/BulkActionModalTypes";
import { WriteUpForm } from "grading/WriteUpForm/WriteUpForm";
import { TeacherEventUnit } from "shared/event/model/TeacherEventUnit";

export const BulkGradingModal = (
	{ unit, onClose }:
	{ unit: TeacherEventUnit, onClose: () => void }) => {
	const eventParticipantTeams: EventParticipant[] = useEventParticipantTeamsToBeGraded(unit);
	const eventParticipantUsers: EventParticipant[] = useEventParticipantUsersToBeGraded(unit);

	return	<WrappedBulkGradingModal
		unit={unit}
		onClose={onClose}
		eventParticipantTeams={eventParticipantTeams}
		eventParticipantUsers={eventParticipantUsers} />;
};

export const WrappedBulkGradingModal = (
	{
		unit,
		onClose,
		eventParticipantUsers,
		eventParticipantTeams,
	}:
	{
		unit: TeacherEventUnit,
		onClose: () => void,
		eventParticipantUsers: EventParticipant[],
		eventParticipantTeams: EventParticipant[],
	}) => {

	const intl = useIntl();
	const dispatch = useDispatch();

	const writeUpWeight = 1 - (unit?.flagWeight ?? 0);

	const [ submitting, setSubmitting ] = useState(false);
	const [ selectedUserIds, setSelectedUserIds ] = useSelectedUserIds(eventParticipantUsers);
	const [ selectedTeamIds, setSelectedTeamIds ] = useSelectedTeamIds(eventParticipantTeams);

	const [ writeUpGrade, setWriteUpGrade ] = useState<number>(1);
	const [ writeUpText, setWriteUpText ] = useState<string>("");
	const [ writeUpFile, setWriteUpFile ] = useState<ProcessedFile>();

	const handleSubmit = submitModal({
		comment: writeUpText,
		grade: writeUpGrade,
		userIds: selectedUserIds,
		teamIds: selectedTeamIds,
		attachment: writeUpFile ? {
			name: writeUpFile?.name,
			content: writeUpFile?.data,
		} : undefined,
	}, setSubmitting, dispatch, unit, onClose);

	const clearModal = () => {
		setSelectedUserIds([]);
		setSelectedTeamIds([]);
		setWriteUpGrade(1);
		setWriteUpText("");
		setWriteUpFile(undefined);
	};

	return (
		<ModalWindow
			title={intl.fm("teacher.grading.bulk.modal.title", undefined, { unit: unit?.title })}
			open={true}
			onClose={onClose}
			classes={{}}
			fullWidth
			maxWidth="md"
			actionSection={(
				<BulkActionModalButtonBar
					intl={intl}
					clearModal={clearModal}
					selectedUserIds={selectedUserIds}
					selectedTeamIds={selectedTeamIds}
					submitModal={handleSubmit}
					isSubmitting={submitting} />
			)}
		>
			<BulkActionModalHeader
				intl={intl}
				eventParticipantTeams={eventParticipantTeams}
				selectedTeamIds={selectedTeamIds}
				onParticipantTeamChange={onParticipantChange(setSelectedTeamIds)}
				eventParticipantUsers={eventParticipantUsers}
				selectedUserIds={selectedUserIds}
				onParticipantUserChange={onParticipantChange(setSelectedUserIds)}
				exportEvent={exportEvent(unit)} />
			{ /* in bulk mode we ignore the flagPoints information, so it's always 0 */}
			<PointSlider
				value={percentage(writeUpGrade)}
				maxPoints={unit?.maxPoints}
				flagPoints={0}
				writeupWeight={writeUpWeight}
				onChange={(grade: number) => setWriteUpGrade(percent(grade))}
				disabled={false}
			/>
			<WriteUpForm
				isTeacher={true}
				isSubmitting={submitting}
				writeUpText={writeUpText}
				writeUpFile={writeUpFile}
				onWriteUpChange={(text: string) => setWriteUpText(text)}
				onFileChange={(file?: ProcessedFile) => setWriteUpFile(file)}
				hasError={false} />
		</ModalWindow>
	);
};



